import axios from "axios";
import store from "@/store";
import { Modal, notification, message } from "antd";
import { getToken, clearLocalDatas } from "@/utils/auth";
import { logout } from "@/store/actions";
import tokenManager, { isTokenExpired } from "@/utils/tokenManager";
import qs from 'qs';

// 错误信息
const codeMessage = {
  200: "服务器成功返回请求的数据。",
  201: "新建或修改数据成功。",
  202: "一个请求已经进入后台排队（异步任务）。",
  204: "删除数据成功。",
  400: "发出的请求有错误，服务器没有进行新建或修改数据的操作。",
  401: "用户没有权限（令牌、用户名、密码错误）。",
  403: "用户得到授权，但是访问是被禁止的。",
  404: "发出的请求针对的是不存在的记录，服务器没有进行操作。",
  406: "请求的格式不可得。",
  410: "请求的资源被永久删除，且不会再得到的。",
  422: "当创建一个对象时，发生一个验证错误。",
  500: "服务器发生错误，请检查服务器。",
  502: "网关错误。",
  503: "服务不可用，服务器暂时过载或维护。",
  504: "网关超时。",
}

// 请求配置文件
const config = {
  // `baseURL` 将自动加在 `url` 前面，除非 `url` 是一个绝对 URL。
  // 它可以通过设置一个 `baseURL` 便于为 axios 实例的方法传递相对 URL
  baseURL: process.env.REACT_APP_API ? process.env.REACT_APP_API : process.env.BASE_URL,

  timeout: 1000 * 15,

  // `withCredentials` 表示跨域请求时是否需要使用凭证
  withCredentials: false,

  // `maxRedirects` 定义在 node.js 中 follow 的最大重定向数目
  // 如果设置为0，将不会 follow 任何重定向
  maxRedirects: 3,
  headers: {
    "Content-Type": "application/json;charset=UTF-8"
  },
}

// 创建ajax实例
const instance = axios.create(config)

// Request interceptor
instance.interceptors.request.use(
  function (config) {
    const token = getToken()

    // 检查token是否过期
    const isLoginRequest = config.url.includes('/api/staffLogin') ||
      config.url.includes('/api/login') ||
      config.url.includes('/api/miniLogin') ||
      config.url.includes('/api/androidLogin');

    if (!isLoginRequest && token && isTokenExpired(token)) {
      clearLocalDatas(["token"]);
      store.dispatch(logout());

      // 取消当前请求
      const error = new Error('Token已过期');
      error.isTokenExpired = true;
      return Promise.reject(error);
    }

    if (token) {
      config.headers["x-access-token"] = token
    }

    return config
  },
  function (error) {
    return Promise.reject(error)
  }
)

// Response interceptor
instance.interceptors.response.use(
  function (response) {
    if (response.data) {
      const { msg, status } = response.data
      if (status === 1) {
        message.error(msg)
      }
    }
    return response && response.data
  },
  function (error) {
    // 处理token过期错误
    if (error.isTokenExpired) {
      setTimeout(() => {
        window.location.hash = '/login';
        window.location.reload();
      }, 100);
      return Promise.reject('登录已过期，请重新登录');
    }

    const { response, message: errMessage } = error
    let errorMessage = ''

    if (response && response.status) {
      const errorText = codeMessage[response.status] || response.statusText
      const { status, config, data } = response

      errorMessage = data.message || errorText

      notification.error({
        message: `请求错误 ${status}: ${config.url}`,
        description: errorMessage,
      })

      if (response.status === 401) {

        // 停止token监控
        tokenManager.stopTokenMonitoring();

        Modal.confirm({
          title: "登录过期确认",
          content: "您的登录已过期，请重新登录。",
          okText: "重新登录",
          cancelText: "取消",
          onOk() {
            const token = store.getState().user.token
            store.dispatch(logout(token))
            setTimeout(() => {
              window.location.hash = '/login';
              window.location.reload()
            }, 1000)
          }
        })
      }
    } else if (!response) {
      errorMessage = errMessage === "Network Error"
        ? "网络错误，请检查客户端是否存在网络故障或服务端无法响应"
        : "客户端出现错误"

      clearLocalDatas(["token"]) // 清除token
      notification.error({
        description: errorMessage,
        message: "状态异常",
      })
    }

    return Promise.reject(errorMessage || '请求失败')
  }
)

// Rewrite GET method to handle query params
const originalGet = instance.get;
instance.get = function (url, data, config) {
  if (data) {
    const query = qs.stringify(data, { addQueryPrefix: true })
    return originalGet.call(this, url + query, config)
  }
  return originalGet.call(this, url, config)
}

// Rewrite DELETE method to handle query params
const originalDelete = instance.delete;
instance.delete = function (url, data, config) {
  if (data) {
    const query = qs.stringify(data, { addQueryPrefix: true })
    return originalDelete.call(this, url + query, config)
  }
  return originalDelete.call(this, url, config)
}

export default instance
